import { PropTypes } from 'prop-types';
import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Checkbox, CheckedState } from '@ge/components/checkbox';
import { Icon, Icons } from '@ge/components/icon';
import { Dialog } from '@ge/components/modal';
import { Text } from '@ge/components/typography';
import useStateRef from '@ge/hooks/state-ref';
import { typography } from '@ge/tokens/typography';
import { toLocaleKey } from '@ge/util/string-utils';

import { CommandsDialogBulkList } from './command-dialog-bulk-list';
import { CommandsDialogHistory } from './command-dialog-history';

const DialogContainer = styled.div`
  width: 460px;
  overflow: hidden;
`;

const FooterButtons = styled.div`
  margin-left: auto;
  text-align: right;
  button:not(:last-of-type) {
    margin-right: 5px;
  }
`;

const AssetDetails = styled.div`
  display: flex;
  margin-top: 6px;
  .spacer {
    margin: 0 8px;
    font-weight: bold;
  }
`;

const DclrContainer = styled.div`
  margin-top: 6px;
  font-size: 12px;
  font-weight: 200;
  line-height: 20px;
  display: inline-block;
  .spacer {
    margin-left: 10px;
    margin-bottom: 5px;
  }
  HeaderText {
    font-weight: bold;
  }
`;

const DclrSegment = styled.div`
  margin-top: 6px;
  margin-bottom: 6px;
  background-color: #2a323b;
  padding: 10px 16px 10px 16px;
  color: #999999;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 30px;
  width: 460px;
`;

const DialogSegment = styled.div`
  margin-bottom: 9px;
  background-color: #2a323b;
  height: 85px;
  margin-top: 20px;
  border-radius: 7px;
  align-content: center;
  padding-left: 20px;
`;

const StyledHeader = styled.div`
  color: ${(props) => props.theme.layout.textColor};
  display: flex;
  align-items: flex-start;
  padding: 0;
`;

const TurbineIcon = styled(Icon).attrs((props) => ({
  size: 14,
  icon: Icons.TURBINE,
  color: props.theme.entityDetails.headerIconColor,
}))`
  margin-right: 5px;
`;

const SiteIcon = styled(Icon).attrs((props) => ({
  size: 14,
  icon: Icons.SITE,
  color: props.theme.entityDetails.headerIconColor,
}))`
  margin-right: 5px;
`;

const ScrollableDiv = styled.div`
  max-height: calc(80vh - 80px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  &::-webkit-scrollbar {
    width: 10px;
    border-radius: 1px;
    background-color: rgba(12, 15, 18, 0.24);
  }
  &::-webkit-scrollbar-thumb {
    height: 100px;
    border: 2px solid transparent;
    background-clip: content-box;
    border-radius: 2.5px;
    background-color: ${(props) => props.theme.table.fixedBorderColor};
  }
`;

const StyledRadioInput = styled.div`
  margin: 16px 0;
  margin-left: -5px;
  input[type='radio'] {
    &:checked:before {
      border-color: ${(props) => props.theme.radio.borderColor};
      animation: ripple 0.2s linear forwards;
    }

    &:checked:after {
      transform: scale(1);
    }
  }

  input[type='radio'] {
    height: 14px;
    position: relative;
    cursor: pointer;
    vertical-align: bottom;

    &:before,
    &:after {
      position: absolute;
      content: '';
      border-radius: 50%;
      transition: all 0.3s ease;
      transition-property: transform, border-color;
    }

    &:before {
      left: 0;
      top: 0;
      width: 13px;
      height: 13px;
      background: #38434e;
      box-shadow: inset 20px -28px 30px 4px rgb(45 45 52 / 50%);
      border: 1px solid ${(props) => props.theme.radio.borderColor};
    }

    &:after {
      top: 4px;
      left: 4px;
      width: 6px;
      height: 6px;
      transform: scale(0);
      background: ${(props) => props.theme.radio.activeColor};
    }
  }
`;

const RadioLabel = styled.span`
  margin-left: 6px;
`;

const StyledCheckbox = styled.div`
  label {
    display: inline-block;
    margin-top: 2px;
  }
  span {
    margin-left: 4px;
    color: ${(props) => props.theme.layout.textColor};
  }
  svg {
    margin-left: 0;
  }
`;

const HeaderText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 1,
}))``;

//adding cmd list here to populate disclaimer text accordingly based on cmd selection
const RebootCmds = [
  {
    canId: 'WTUR.Cmd.GE.PitchAxis1Reboot',
    desc: 'Pitch Axis 1 Reboot',
    commandId: 'pitchAxis1Reboot',
  },
  {
    canId: 'WTUR.Cmd.GE.PitchAxis2Reboot',
    desc: 'Pitch Axis 2 Reboot',
    commandId: 'pitchAxis2Reboot',
  },
  {
    canId: 'WTUR.Cmd.GE.PitchAxis3Reboot',
    desc: 'Pitch Axis 3 Reboot',
    commandId: 'pitchAxis3Reboot',
  },
  {
    canId: 'WTUR.Cmd.GE.ControllerReboot',
    desc: 'Controller Reboot',
    commandId: 'controllerReboot',
  },
  {
    canId: 'WTUR.Cmd.GE.SafetyChainReset',
    desc: 'Safety Chain Reset',
    commandId: 'safetyChainReset',
  },
];

const PitchAxistHeader = 'Pitch Axis Reboot';

export const CommandsDialog = ({
  assets,
  onClose,
  onAbort,
  onExecute,
  command,
  status,
  counts,
  isExecuting,
  isGroupCommandComplete,
}) => {
  const { t } = useTranslation(['entity-details']);

  const [abortDisabled, setAbortDisabled] = useState(false);
  const [radioSelected, setradioSelected] = useState(null);
  const [isChecked, setIsChecked, isCheckedRef] = useStateRef(CheckedState.UNCHECKED);

  const isSingleCommand = assets?.length === 1;

  const isPitchAxis = command?.commandId?.includes('pitchAxis') ? true : false;
  const getPitchAxisSel = RebootCmds?.find((item) => item.commandId === radioSelected);
  let cmdName = command?.desc;
  if (isPitchAxis) {
    command = getPitchAxisSel ? getPitchAxisSel : command;
    cmdName = PitchAxistHeader;
  }
  const enableDsclmr = RebootCmds?.find((item) => item.commandId === command?.commandId)
    ? true
    : false;
  const enablePitchDsclmr = isPitchAxis && !radioSelected ? false : true;

  const [excuteDisabled, setExcuteDisabled] = useState(enableDsclmr ? true : false);

  const handleCheckbox = useCallback(
    (checked) => {
      const nextChecked = checked ? CheckedState.CHECKED : CheckedState.UNCHECKED;

      setIsChecked(nextChecked);

      if (isPitchAxis) {
        setExcuteDisabled(!(nextChecked === CheckedState.CHECKED && radioSelected != null));
      } else {
        setExcuteDisabled(nextChecked !== CheckedState.CHECKED);
      }
    },
    [isPitchAxis, radioSelected, setIsChecked],
  );

  const handlePitchAxisChange = useCallback(
    (axisCommandId) => {
      setradioSelected(axisCommandId);
      if (isCheckedRef.current === CheckedState.CHECKED) {
        setExcuteDisabled(false);
      }
    },
    [isCheckedRef],
  );

  const handleExecute = useCallback(
    (cmdVal) => {
      setAbortDisabled(false);
      setExcuteDisabled(true);
      setIsChecked(CheckedState.UNCHECKED);
      // Todo: see if we need to be passed from the data hook
      onExecute(cmdVal);
      if (isSingleCommand) {
        onClose();
      }
    },
    [isSingleCommand, onClose, onExecute, setIsChecked],
  );

  const handleAbort = useCallback(async () => {
    await onAbort();
    setAbortDisabled(true);
  }, [onAbort]);

  const getFooter = useMemo(() => {
    return (
      <FooterButtons>
        {isGroupCommandComplete ? (
          <>
            <Button onClick={onClose}>{t('general:close', 'Close')}</Button>
            <Button primary disabled={true} onClick={handleAbort}>
              {t('general:abort', 'Abort')}
            </Button>
          </>
        ) : isExecuting ? (
          <>
            <Button onClick={onClose}>{t('general:close', 'Close')}</Button>
            <Button primary disabled={abortDisabled} onClick={handleAbort}>
              {t('general:abort', 'Abort')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>{t('general:cancel', 'Cancel')}</Button>
            <Button primary disabled={excuteDisabled} onClick={() => handleExecute(command)}>
              {t('general:execute', 'Execute')}
            </Button>
          </>
        )}
      </FooterButtons>
    );
  }, [
    isGroupCommandComplete,
    onClose,
    t,
    handleAbort,
    isExecuting,
    abortDisabled,
    excuteDisabled,
    handleExecute,
    command,
  ]);

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      onConfirm={onExecute}
      header={`${t(`commands.${toLocaleKey(cmdName)}`, cmdName ?? '')} Confirmation`}
      footer={getFooter}
      contentWidth
    >
      <DialogContainer>
        <HeaderText>
          {t('general:commands.attempting_msg', 'You are attempting to')}:{' '}
          <b>{t(`commands.${toLocaleKey(cmdName)}`, cmdName ?? '')}</b>
        </HeaderText>
        <ScrollableDiv>
          <AssetDetails>
            <StyledHeader onClick={() => {}}>
              <SiteIcon />
              <HeaderText>
                <b>{assets[0].site.name}</b>
              </HeaderText>
            </StyledHeader>
            <HeaderText className="spacer">{'/'}</HeaderText>
            <StyledHeader onClick={() => {}}>
              <TurbineIcon />
              <HeaderText>
                <b>{isSingleCommand ? assets[0].name : `${assets.length} Assets`}</b>
              </HeaderText>
            </StyledHeader>
          </AssetDetails>

          {isPitchAxis ? (
            <DialogSegment>
              <HeaderText className="spacer">{'Select Pitch Axis to be rebooted:'}</HeaderText>
              <RadioGroup>
                <StyledRadioInput>
                  <input
                    type="radio"
                    value={t('pitchAxis1Reboot', 'pitchAxis1Reboot')}
                    name={'axis1'}
                    checked={radioSelected === 'pitchAxis1Reboot' ? true : false}
                    onChange={() => handlePitchAxisChange('pitchAxis1Reboot')}
                  />
                  <RadioLabel>{t('Axis 1', 'Axis 1')}</RadioLabel>
                </StyledRadioInput>
                <StyledRadioInput>
                  <input
                    type="radio"
                    value={t('pitchAxis2Reboot', 'pitchAxis2Reboot')}
                    name={'axis2'}
                    checked={radioSelected === 'pitchAxis2Reboot' ? true : false}
                    onChange={() => handlePitchAxisChange('pitchAxis2Reboot')}
                  />
                  <RadioLabel>{t('Axis 2', 'Axis 2')}</RadioLabel>
                </StyledRadioInput>
                <StyledRadioInput>
                  <input
                    type="radio"
                    value={t('pitchAxis3Reboot', 'pitchAxis3Reboot')}
                    name={'axis3'}
                    checked={radioSelected === 'pitchAxis3Reboot' ? true : false}
                    onChange={() => handlePitchAxisChange('pitchAxis3Reboot')}
                  />
                  <RadioLabel>{t('Axis 3', 'Axis 3')}</RadioLabel>
                </StyledRadioInput>
              </RadioGroup>
            </DialogSegment>
          ) : (
            ''
          )}

          {enableDsclmr && enablePitchDsclmr ? (
            <DclrSegment>
              <HeaderText className="spacer">{'Disclaimer'}</HeaderText>
              <DclrContainer>
                <div>
                  {command?.commandId?.includes('safetyChainReset') ? (
                    <div>
                      Uncontrolled remote resets of the safety chain can result in equipment damage
                      or pose a risk to field personnel. Initiate a remote reset of the safety chain
                      only after carefully following the steps outlined in the Fault Handling
                      Procedures for safety chain-related trips.
                    </div>
                  ) : (
                    <div>
                      Uncontrolled controller or pitch axis reboots can result in equipment damage
                      or pose a risk to field personnel. Initiate a reboot only after carefully
                      checking that the machine conditions.
                    </div>
                  )}
                  <div> By confirming this message, I certify that I have:</div>
                </div>
                {command?.commandId?.includes('safetyChainReset') ? (
                  <div className="spacer">
                    <list>
                      <li>
                        Followed the Fault Handling Procedures for safety chain-related trips.
                      </li>
                      <li>
                        Analyzed the fault scenario and determined it is eligible for a remote
                        reset.
                      </li>
                    </list>
                  </div>
                ) : (
                  <div className="spacer">
                    <list>
                      <li>Checked that the machine is not in Service / Repair </li>
                      <li>
                        Checked that the machine is idling with the high-speed shaft brake open
                      </li>
                      <li>Checked that at least two blades are at the limit switches</li>
                    </list>
                  </div>
                )}
                <div>
                  Failure to adhere to these procedures may lead to serious consequences. Proceed
                  with caution.
                </div>
              </DclrContainer>
              <div className="Checkbox">
                <StyledCheckbox>
                  <Checkbox label={''} checkState={isChecked} onChange={handleCheckbox} />
                  <span>I accept and confirm this message</span>
                </StyledCheckbox>
              </div>
            </DclrSegment>
          ) : (
            ''
          )}

          {isSingleCommand ? (
            <CommandsDialogHistory asset={assets[0]} />
          ) : (
            <CommandsDialogBulkList
              status={status}
              counts={counts}
              setAbortDisabled={setAbortDisabled}
            />
          )}
        </ScrollableDiv>
      </DialogContainer>
    </Dialog>
  );
};

CommandsDialog.propTypes = {
  assets: PropTypes.instanceOf(Array).isRequired,
  onClose: PropTypes.func,
  onAbort: PropTypes.func,
  onExecute: PropTypes.func,
  command: PropTypes.object.isRequired,
  history: PropTypes.instanceOf(Object),
  status: PropTypes.instanceOf(Array),
  counts: PropTypes.instanceOf(Object),
  isExecuting: PropTypes.bool,
  isGroupCommandComplete: PropTypes.bool,
};

CommandsDialog.defaultValues = {
  onClose: () => null,
  onExecute: () => null,
  onAbort: () => null,
  history: null,
  status: null,
  counts: {},
  isExecuting: false,
  isGroupCommandComplete: false,
};
